<template>
	<HeroSection />

	<TimJackSection />

	<StocksWatchingSection :news-list="news_list" />

	<MakeMoveSection />

	<TradingGuidesList />

	<SykesOnYoutube />

	<TrustPilotSection />

	<Head>
		<Title>Timothy Sykes - Millionaire Penny Stock Trader</Title>
		<Link
			rel="canonical"
			:href="$config.public.current_hostname"
		/>
		<Meta
			name="description"
			content="Timothy Sykes is a millionaire penny stock trader and entrepreneur. He is best known for earning $1.65 million by day trading while attending Tulane University."
		/>
		<Meta
			content="en_US"
			property="og:locale"
		/>
		<Meta
			content="article"
			property="og:type"
		/>
		<Meta
			content="Timothy Sykes : Millionaire Penny Stock Trader"
			property="og:title"
		/>
		<Meta
			content="Timothy Sykes is a millionaire penny stock trader and entrepreneur. 
He is best known for earning $1.65 million by day trading while attending Tulane University."
			property="og:description"
		/>
		<Meta
			:content="$config.public.current_hostname"
			property="og:url"
		/>
		<Meta
			content="Timothy Sykes"
			property="og:site_name"
		/>
		<Meta
			content="2022-04-18T07:17:16+00:00"
			property="article:modified_time"
		/>
		<Meta
			content="https://content.timothysykes.com/wp-content/uploads/2019/06/Conclusion-How-to-Read-Stock-Charts.jpg"
			property="og:image"
		/>
		<Meta
			name="twitter:card"
			content="summary_large_image"
		/>
		<Meta
			name="twitter:site"
			content="@timothysykes"
		/>
	</Head>
</template>

<script setup>
import { store } from "../utils/store";
import {
	fetchStocksNewsList,
	toHomepageNewsCard
} from "@/utils/stocks-news.js";
import { fetchNewsFeedTopGainers } from "@/utils/news-feed-client.js";
import { useStocksStore } from "@/stores/useStocksStore";

const $config = useRuntimeConfig();
const stocksStore = useStocksStore();

// Fetched during SSR so the news cards render on first paint instead of
// waiting for a client-only round trip. fetchStocksNewsList already prefers
// the flattened, pagination-free `news-feed/latest-news` bundle (see
// utils/stocks-news.js) and falls back to the original paginated endpoint.
const { data: newsListData } = await useAsyncData(
	"homepage-news-list",
	async () => {
		try {
			const items = await fetchStocksNewsList({ config: $config, limit: 20 });
			return items.map(toHomepageNewsCard);
		} catch (error) {
			console.error("[index.vue] Error fetching news:", error);
			return [];
		}
	}
);

const news_list = computed(() => newsListData.value || []);

// Flattened, parameter-free bundle of the top-gainers list + 1D sparklines.
// Seeds useStocksStore directly (primeTopGainerStocksFromBundle) so
// TopGainerStocks (rendered by StocksWatchingSection, not wrapped in
// ClientOnly here) renders real data on the very first server-rendered
// paint instead of a loading skeleton. Falls back to TopGainerStocks' own
// client-side fetch if this bundle fails or comes back empty.
const { data: newsFeedTopGainersData } = await useAsyncData(
	"homepage-news-feed-top-gainers",
	() =>
		fetchNewsFeedTopGainers({ config: $config }).catch((err) => {
			console.warn(
				"[index.vue] news-feed/top-gainers bundled fetch failed, TopGainerStocks will fall back individually:",
				err?.message || err
			);
			return null;
		})
);

stocksStore.primeTopGainerStocksFromBundle(newsFeedTopGainersData.value, 20);
useHead(() => {
	return {
		meta: [
			{
				hid: "trustpilot-title",
				name: "trustpilot-title",
				content: "Trustpilot Reviews - Timothy Sykes"
			},
			{
				hid: "trustpilot-description",
				name: "trustpilot-description",
				content: "Read reviews from our satisfied customers on Trustpilot."
			}
		],
		script: [
			{
				async: true,
				type: "text/javascript",
				src: "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
			}
		]
	};
});
onMounted(() => {
	// Reset loading state when home page mounts to hide the loading indicator
	store.manualToggleLoading(false);
});
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.case-study-section {
	background: #142960;
	background-image: url("/images/assets/texture-2.svg");
	background-position: center;
	background-repeat: repeat;
	background-size: contain;
	h2.subtitle {
		color: white;
		margin-bottom: 24px;

		@include breakpoint(lg) {
			margin-bottom: 56px;
		}
	}
}

.student-case-study {
	display: flex;
	padding: 24px;
	border-radius: 8px;
	background-color: white;
	gap: 16px;
	flex-direction: column;
	align-items: center;
	text-align: center;

	p {
		font-size: 20px;

		@include breakpoint(lg) {
			font-size: 24px;
		}
	}

	img {
		height: 136px;
		width: 136px;
		border-radius: 8px;
	}

	@include breakpoint(lg) {
		flex-direction: row;
		text-align: left;
	}
}

.case-study-link {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: #eb5528;
	font-family: Fira Sans;
	font-size: 18px;
	font-style: normal;
	font-weight: 600;
	line-height: 25px;
	text-align: center;
	justify-content: center;

	@include breakpoint(lg) {
		text-align: left;
		justify-content: flex-start;
	}

	/* 138.889% */
	&-icon {
		height: 32px;
		width: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
		border-radius: 16px;
		background-color: #eb5528;
	}
}

.ts-feature-block {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 24px 16px;
	background-color: white;
	border-radius: 8px;
	align-items: center;
	text-align: center;
	position: relative;

	@include breakpoint(xl) {
		padding: 24px 8px;

		&:nth-child(1) {
			left: 40px;
		}

		&:nth-child(2) {
			left: 40px;
		}
	}

	img {
		max-width: 80px;
	}
}

.home-collage-1 {
	position: relative;
	height: 340px;
	max-width: 280px;
	width: 280px;

	@include breakpoint(md) {
		height: 376px;
		max-width: 370px;
		width: 370px;
	}
	@include breakpoint(lg) {
		height: 540px;
		max-width: unset;
		width: unset;
	}

	img {
		border-radius: 8px;
		box-sizing: content-box;
		border-radius: 8px;
		outline-offset: -4px;
		outline: 8px solid black;
	}

	img:nth-child(1) {
		position: absolute;
		left: -220px;
		z-index: 1;
		margin: auto;
		top: 120px;
	}

	img:nth-child(2) {
		position: absolute;
		right: 0;
		z-index: 1;
		margin: auto;
		top: 0;
	}
}

.trading-benefits {
	padding-top: 24px;
	padding-bottom: 24px;
	outline: 4px solid #eff6ff;
	border-radius: 12px;
	margin: 0 12px;
	max-width: 400px;
	@include breakpoint(lg) {
		padding-top: 0;
		padding-bottom: 0;
		outline: unset;
		margin: auto;
	}
}

.home-collage {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	margin-top: 64px;

	@include breakpoint(lg) {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 20px;
		margin-top: 86px;
	}
}
</style>
